import { useTranslation } from "react-i18next";
import { Database } from "lucide-react";
import { NeuCard } from "../../../components/neu/Neu";

const Bar = ({ percent, tone }) => (
  <div className="mt-1.5 h-2 rounded-full bg-primary/10 shadow-neu-in">
    <div className={`h-2 rounded-full ${tone}`} style={{ width: `${Math.min(percent, 100)}%` }} />
  </div>
);

const Gauge = ({ label, percent, hint }) => (
  <div className="rounded-2xl bg-background p-3 shadow-neu-sm">
    <div className="flex items-center justify-between gap-2">
      <p className="text-[10px] font-bold text-muted-foreground">{label}</p>
      <p className="font-mono text-sm font-black">{percent}%</p>
    </div>
    <Bar percent={percent} tone={percent > 85 ? "bg-destructive" : percent > 60 ? "bg-amber-500" : "bg-primary"} />
    {hint ? <p className="mt-1 font-mono text-[10px] text-muted-foreground num">{hint}</p> : null}
  </div>
);

export const StorageCard = ({ data, onOpen }) => {
  const { t } = useTranslation();
  if (!data) return null;

  return (
    <NeuCard
      data-testid="system-storage-card"
      onClick={onOpen}
      className="cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
    >
      <p className="label-xs flex items-center gap-2">
        <Database className="h-4 w-4 text-primary" /> {t("system.storage")}
      </p>

      <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
        <Gauge label={t("system.disk")} percent={data.disk_used_percent} hint={t("system.freeGb", { gb: data.disk_free_gb })} />
        <Gauge label={t("system.memory")} percent={data.memory_percent} hint={`${data.memory_used_mb} MB`} />
        <Gauge label={t("system.cpu")} percent={data.cpu_percent} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {[
          [t("system.dbSize"), `${data.db_size_mb} MB`],
          [t("system.indexSize"), `${data.index_size_mb} MB`],
          [t("system.documents"), data.documents.toLocaleString("en-GB")],
          [t("system.collections"), data.collection_count],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl bg-background p-3 shadow-neu-sm">
            <p className="text-[10px] font-bold text-muted-foreground">{label}</p>
            <p className="mt-1 font-mono text-base font-black num">{value}</p>
          </div>
        ))}
      </div>

      <ul className="mt-4 grid gap-1.5 font-mono text-[11px] sm:grid-cols-2" data-testid="system-collections">
        {data.collections.map((c) => (
          <li key={c.name} className="flex items-center justify-between gap-3 rounded-xl bg-background px-3 py-2 shadow-neu-sm">
            <span className="truncate text-muted-foreground">{c.name}</span>
            <span className="font-black text-primary">{c.documents}</span>
          </li>
        ))}
      </ul>
    </NeuCard>
  );
};
